import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DROPDOWN_ITEM_CLASS, DROPDOWN_PANEL_CLASS } from '@/styles/floatingPanel';
import { AuthInput } from './AuthInput';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { isNetworkError, resolveApiErrorMessage } from '@/utils/errors';
import {
  FORGOT_PASSWORD_MESSAGE,
  PASSWORD_MASK_CHARACTER,
  PASSWORD_MASK_LENGTH,
  SITE_LABEL,
  SITE_VALUES,
} from '../constants/auth.constants';
import { useLogin } from '../hooks/useLogin';
import { useRememberedSite } from '../hooks/useRememberedSite';
import { useRememberedUsername } from '../hooks/useRememberedUsername';
import { loginPayloadSchema, type LoginPayload } from '../types/auth.types';

const PASSWORD_PLACEHOLDER = PASSWORD_MASK_CHARACTER.repeat(PASSWORD_MASK_LENGTH);

function getInlineLoginError(error: unknown): string | null {
  if (!error || isNetworkError(error)) return null;
  return resolveApiErrorMessage(error, 'Login failed');
}

export function LoginForm() {
  const { mutate, isPending, error } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const { rememberedUsername, isRemembering, setIsRemembering, persistUsername } =
    useRememberedUsername();
  const { rememberedSite, persistSite } = useRememberedSite();
  const inlineError = getInlineLoginError(error);

  const form = useForm<LoginPayload>({
    resolver: zodResolver(loginPayloadSchema),
    defaultValues: { username: rememberedUsername, password: '', tenant: rememberedSite },
  });

  function handleLogin(values: LoginPayload) {
    mutate(values, {
      onSuccess: () => {
        persistUsername(values.username);
        persistSite(values.tenant);
      },
    });
  }

  function handleForgotPassword() {
    toast.info(FORGOT_PASSWORD_MESSAGE);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleLogin)} className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="tenant"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Site</FormLabel>
              <Select<string>
                items={SITE_VALUES.map((site) => ({ value: site, label: SITE_LABEL[site] }))}
                value={field.value}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger className="h-12.5 w-full rounded-lg" autoFocus={!rememberedSite}>
                    <SelectValue placeholder="Pick a site" />
                    <SelectIcon />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className={DROPDOWN_PANEL_CLASS}>
                  {SITE_VALUES.map((site) => (
                    <SelectItem key={site} value={site} className={DROPDOWN_ITEM_CLASS}>
                      {SITE_LABEL[site]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="username"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Username</FormLabel>
              <FormControl>
                <AuthInput
                  placeholder="Username"
                  autoComplete="username"
                  autoFocus={Boolean(rememberedSite)}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between gap-2">
                <FormLabel>Password</FormLabel>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <FormControl>
                <div className="relative">
                  <AuthInput
                    type={showPassword ? 'text' : 'password'}
                    placeholder={showPassword ? 'Password' : PASSWORD_PLACEHOLDER}
                    autoComplete="current-password"
                    className="pr-10"
                    {...field}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center gap-2">
          <Checkbox
            id="remember-me"
            className="size-5"
            checked={isRemembering}
            onCheckedChange={(checked) => setIsRemembering(checked === true)}
          />
          <Label htmlFor="remember-me" className="text-sm font-normal text-muted-foreground">
            Remember me
          </Label>
        </div>

        {inlineError && (
          <div
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {inlineError}
          </div>
        )}

        <Button
          type="submit"
          disabled={isPending}
          className="h-12.5 w-full rounded-lg text-base font-semibold"
        >
          {isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </Button>
      </form>
    </Form>
  );
}
